import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';

import { ButtonProps } from './Button.types';

import { getClickTrackValue, isDcaTrackingPayloadReady, trackImpression } from '../../utils/TrackingPropertyUtils';

import { constants } from '../../constants/ApplicationConstants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Button = forwardRef((props: ButtonProps, ref?: any) => {
  // WEB-191033: Many consumers (e.g. MerchandisingCoBrandCard) use <Button> without
  // passing a ref. Previously the tracking `useEffect` below read through the external
  // `ref`, which was `null` in that case; `getClickTrackValue` then short-circuited on
  // every optional-chained `ref?.current.*` lookup and silently stamped no attributes
  // (no `data-custom_click_track_value`, no `track-completed`). Mirror the `Link`
  // pattern: always own an internal ref so the effect has a DOM node to write to, and
  // forward the same node upward via `useImperativeHandle` so any parent that DOES
  // pass a ref still reads the real element.
  const internalButtonRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null);
  useImperativeHandle(ref, () => internalButtonRef.current);
  const buttonRef = internalButtonRef as React.RefObject<HTMLAnchorElement | HTMLButtonElement>;
  const {
    type = 'button',
    buttonCopy = '',
    style,
    className,
    callback,
    isLink = false,
    href,
    target,
    rel,
    children,
    trackingProperties,
    linkType = 'external',
    isDisabled = false,
    testId = '',
    isTextBeforeChildren = true,
    compName,
    custom_click_track_value,
    linkAriaLabelOpenNewWindow = 'opens in new window',
    ariaExpanded = undefined,
    ariaPressed = undefined,
    ariaLabel = '',
    ariaRole,
    title,
    ariaDescribedBy,
    ariaLabelledBy,
    ...restProps
  } = props;

  const { COBRAND_NON_POINTS_MATH, TARGET_BLANK } = constants;
  // WEB-191033: Prefer an explicit boolean `isCoBrand` from trackingProperties when provided
  // (e.g. normalizers that drive cobrand-only cards). Fall back to deriving from `type` or
  // `merchandisingCategory` for legacy consumers that don't set `isCoBrand` directly.
  const trackingProps = {
    ...trackingProperties,
    isCoBrand:
      typeof trackingProperties?.isCoBrand === 'boolean'
        ? trackingProperties.isCoBrand
        : trackingProperties?.type
        ? trackingProperties?.type === 'CoBrand'
        : trackingProperties?.merchandisingCategory === COBRAND_NON_POINTS_MATH,
  };
  // WEB-191033: DCA-driven CTAs render BEFORE the Adobe Target mbox resolves.
  // The first invocation of this effect sees empty tracking values; if we stamp
  // `track-completed` in that state, the re-render that arrives with real Target
  // content is gated out and Adobe Launch never sees the real payload. So:
  //   1. Depend on the tracking inputs so the effect re-runs when Target resolves.
  //   2. Gate DCA elements with a content-ready check — defer stamping until the
  //      payload is meaningful. Non-DCA (legacy) elements keep the original
  //      mount-only behavior so global nav / header CTAs aren't regressed.
  useEffect(() => {
    const isTrackingComplete = buttonRef?.current?.classList?.contains('track-completed');
    const currentClickTrackValue = buttonRef?.current?.getAttribute('data-custom_click_track_value') ?? '';
    const expectedClickTrackPrefix =
      trackingProps?.trackingContentPosition && trackingProps?.trackingDescription
        ? `${trackingProps.trackingContentPosition}|${trackingProps.trackingDescription}|`
        : '';
    const shouldRefreshCompletedClickTrack =
      isTrackingComplete &&
      trackingProps?.isDCAEnabled !== true &&
      trackingProps?.clickTrack === true &&
      !custom_click_track_value &&
      expectedClickTrackPrefix.length > 0 &&
      currentClickTrackValue.length > 0 &&
      !currentClickTrackValue.startsWith(expectedClickTrackPrefix);

    if (isTrackingComplete && !shouldRefreshCompletedClickTrack) return;

    if (!isDcaTrackingPayloadReady(trackingProps, custom_click_track_value)) return;

    if (!custom_click_track_value) {
      getClickTrackValue(linkType, trackingProps, buttonRef);
    }
    if (!isTrackingComplete && trackingProps?.impressionTrack && !trackingProps?.clickTrackValSeparator)
      trackImpression(trackingProps || {}, buttonCopy, props?.preTrackVal);
    buttonRef?.current?.classList?.add('track-completed');
  }, [trackingProperties, custom_click_track_value, linkType, buttonCopy, props?.preTrackVal]);

  let ariaLabelCopy = target === TARGET_BLANK ? `${buttonCopy || ariaLabel} ${linkAriaLabelOpenNewWindow}` : buttonCopy;
  ariaLabelCopy = ariaLabelCopy && ariaLabelCopy?.trim();
  const classNames = Array.isArray(className) ? className.join(' ') : className ?? '';
  const isPrimaryOrSecondary = classNames.includes('m-button-primary') || classNames.includes('m-button-secondary');

  const renderLink = () => {
    return (
      <a
        data-component-name="a-ui-library-Button"
        data-testid={testId}
        id={props?.id}
        href={href}
        onClick={callback}
        target={target}
        className={clsx(
          className,
          'custom_click_track',
          trackingProps?.impressionTrack === false ? 'no-impression' : ''
        )}
        rel={rel}
        ref={internalButtonRef as React.RefObject<HTMLAnchorElement>}
        data-tracking-content-position={trackingProps?.trackingContentPosition}
        data-tracking-offer-type={trackingProps?.trackingOfferType}
        data-at-c-ce-var48={trackingProps?.atCCeVar48}
        data-tracking-description={trackingProps?.trackingDescription}
        data-tracking-tag={trackingProps?.trackingTag}
        data-comp-name={compName}
        data-custom_click_track_value={custom_click_track_value}
        data-is-co-brand={trackingProps?.isCoBrand}
        data-is-click-track={trackingProps?.clickTrack}
        data-is-impression-track={trackingProps?.impressionTrack}
        data-is-impression-count={trackingProps?.impressionCount}
        data-additional-tracking-variables={trackingProps?.additionalTrackingVariables}
        data-faic-mcd={trackingProps?.faicMCD}
        data-faic-href={trackingProps?.faicUrl}
        data-meta-href={trackingProps?.metaUrl}
        aria-label={ariaLabelCopy || ariaLabel}
        aria-expanded={ariaExpanded}
        aria-pressed={ariaPressed}
        data-is-auth={trackingProps?.isAuth}
        role={ariaRole}
        title={title}
        data-is-dca-enabled={!!trackingProps?.isDCAEnabled}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        onKeyDown={e => {
          if (isPrimaryOrSecondary && (e.key === ' ' || e.key === 'Spacebar')) {
            e.preventDefault();
            window.location.href = href ?? '';
          }
        }}
        {...restProps}
      >
        {isTextBeforeChildren && `${buttonCopy}`}
        {children}
        {!isTextBeforeChildren && `${buttonCopy}`}
      </a>
    );
  };

  return isLink ? (
    renderLink()
  ) : (
    <button
      data-component-name="a-ui-library-Button"
      data-testid={testId}
      id={props?.id}
      disabled={isDisabled}
      ref={internalButtonRef as React.RefObject<HTMLButtonElement>}
      type={type}
      style={style}
      className={clsx(
        className,
        'custom_click_track',
        isDisabled && 'disabled',
        trackingProps?.impressionTrack === false ? 'no-impression' : ''
      )}
      onClick={callback}
      aria-label={ariaLabelCopy || ariaLabel}
      data-tracking-content-position={trackingProps?.trackingContentPosition}
      data-tracking-offer-type={trackingProps?.trackingOfferType}
      data-at-c-ce-var48={trackingProps?.atCCeVar48}
      data-tracking-description={trackingProps?.trackingDescription}
      data-tracking-tag={trackingProps?.trackingTag}
      data-is-co-brand={trackingProps?.isCoBrand}
      data-is-click-track={trackingProps?.clickTrack}
      data-custom_click_track_value={custom_click_track_value}
      data-is-impression-track={trackingProps?.impressionTrack}
      data-is-impression-count={trackingProps?.impressionCount}
      data-additional-tracking-variables={trackingProps?.additionalTrackingVariables}
      data-faic-mcd={trackingProps?.faicMCD}
      aria-expanded={ariaExpanded}
      aria-pressed={ariaPressed}
      data-is-auth={trackingProps?.isAuth}
      title={title}
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
      role={ariaRole}
      {...restProps}
    >
      {isTextBeforeChildren && `${buttonCopy}`}
      {children}
      {!isTextBeforeChildren && `${buttonCopy}`}
    </button>
  );
});
